/**
 * COUNTRIES HOOK - API Methods & React Query Hooks Summary
 *
 * ===========================================
 * API METHODS (Internal Functions):
 * ===========================================
 * • getAllCountries()     - GET /countries - Fetch all countries
 *
 * ===========================================
 * REACT QUERY HOOKS (Exported):
 * ===========================================
 * • useCountries(enabled?)     - Query: Get all countries
 *
 * ===========================================
 * KEY FEATURES:
 * ===========================================
 * • No authentication required
 * • Long cache time (1 hour) as countries data is static
 * • Uses EOR API URL for data fetching
 * • Comprehensive error handling
 * ===========================================
 */

;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import { type UseQueryResult, useQuery } from '@tanstack/react-query';
import type { Country } from '@workspace/ui/types';
import fetchAPI from '@/lib/fetch-api';

// ==================== API FUNCTIONS ====================

/**
 * Fetches all countries from the API
 * @throws {Error} If the API request fails
 * @returns {Promise<Country[]>} The list of countries
 */
export const getAllCountries = async (): Promise<Country[]> => {
  const apiUrl = process.env.NEXT_PUBLIC_EOR_API_URL;

  if (!apiUrl) {
    throw new Error('NEXT_PUBLIC_EOR_API_URL environment variable is not set');
  }

  const response = await fetchAPI<Country[]>(`${apiUrl}/countries`, {
    method: 'GET',
    cache: 'force-cache',
    next: {
      revalidate: 3600, // 1 hour
      tags: ['countries'],
    },
  });

  if (!response.success && (!response.status || ![200, 201].includes(response.status))) {
    throw new Error(response.error || response.message || 'Failed to fetch countries');
  }

  if (!response.data || !Array.isArray(response.data)) {
    throw new Error('Invalid response format from countries API');
  }

  return response.data!;
};

// ==================== REACT QUERY HOOKS ====================

/**
 * Hook to fetch all countries
 * @param {boolean} enabled - Whether the query should be enabled
 * @returns {UseQueryResult} Query result for countries list
 */
export function useCountries(enabled: boolean = true): UseQueryResult<Country[], Error> {
  return useQuery({
    queryKey: ['countries'],
    queryFn: getAllCountries,
    enabled,
    staleTime: 3600000, // 1 hour
    gcTime: 3600000, // 1 hour garbage collection time
    refetchOnWindowFocus: false, // Prevent refetch on window focus
    refetchOnReconnect: false, // Prevent refetch on network reconnect
    refetchInterval: false, // Disable automatic refetching
  });
}
